// src/App.js
import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PremiumRoute from "./components/PremiumRoute";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import PermissionRoute, { AccessProvider } from "./components/PermissionRoute";
import Layout from "./components/Layout";
import UserAuditTracker from "./components/UserAuditTracker";
import { fetchContentProtection } from "./services/contentProtection";
import { getCurrentUser } from "./services/auth";
import LoadingSpinner from "./components/LoadingSpinner";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay";

const Login = lazy(
  () => import("./pages/Login"),
);
const Signup = lazy(
  () => import("./pages/Signup"),
);
const ForgotPassword = lazy(
  () =>
    import("./pages/ForgotPassword"
    ),
);
const ResetPassword = lazy(
  () =>
    import("./pages/ResetPassword"
    ),
);

// Pages accessible to all authenticated users
const CardView = lazy(
  () => import("./pages/CardView"),
);
const BarcodeLookup = lazy(
  () =>
    import("./pages/BarcodeLookup"
    ),
);
const AdminBarcodeScanner = lazy(
  () =>
    import("./pages/AdminBarcodeScanner"
    ),
);
const ProductDetails = lazy(
  () =>
    import("./pages/ProductDetails"
    ),
);

const ShoppingList = lazy(
  () =>
    import("./pages/MyShoppingList"
    ),
);
const RecentPurchases = lazy(
  () =>
    import("./pages/RecentPurchases"
    ),
);
const WatchList = lazy(
  () => import("./pages/SavedItems"),
);

const Meals = lazy(
  () => import("./pages/Meals"),
);
const Planner = lazy(
  () => import("./pages/Planner"),
);
const ChatMealPlanner = lazy(
  () =>
    import("./pages/ChatMealPlanner"
    ),
);
const ChatMealPlannerBuilder = lazy(
  () =>
    import("./pages/ChatMealPlannerBuilder"
    ),
);
const ChatMealPlannerAdvanced = lazy(
  () =>
    import("./pages/ChatMealPlannerAdvanced"
    ),
);
const MealSessionPage = lazy(
  () =>
    import("./pages/MealSessionPage"
    ),
);
const MealPrepSessionPage = lazy(
  () =>
    import("./pages/MealPrepSessionPage"
    ),
);
const Profile = lazy(
  () => import("./pages/Profile"),
);
const Subscribe = lazy(
  () => import("./pages/Subscribe"),
);
const PremiumContent = lazy(
  () =>
    import("./pages/PremiumContent"
    ),
);
const SubscriptionSettings = lazy(
  () =>
    import("./pages/SubscriptionSettings"
    ),
);
const ContactPage = lazy(
  () => import("./pages/Contact"),
);
const StripeAdminPage = lazy(
  () =>
    import("./pages/StripeAdminPage"
    ),
);
const SubscriptionManager = lazy(
  () =>
    import("./pages/SubscriptionManager"
    ),
);
const PackagePermissionsPage = lazy(
  () =>
    import("./pages/PackagePermissionsPage"
    ),
);
const UsersAdminPage = lazy(
  () =>
    import("./pages/UsersAdminPage"),
);
const UserAuditLogPage = lazy(
  () =>
    import("./pages/UserAuditLogPage"
    ),
);
const ProductAnalyticsPage = lazy(
  () =>
    import("./pages/ProductAnalyticsPage"),
);
const UserEditPage = lazy(
  () => import("./pages/UserEditPage"),
);
const StripeEventsPage = lazy(
  () =>
    import("./pages/StripeEventsPage"
    ),
);
const StripeEventDetailPage = lazy(
  () =>
    import("./pages/StripeEventDetailPage"
    ),
);
const StripeAnalyticsPage = lazy(
  () =>
    import("./pages/StripeAnalyticsPage"
    ),
);
const SupportTicketsAdmin = lazy(
  () =>
    import("./pages/SupportTicketsAdmin"
    ),
);
const SupportTicketNew = lazy(
  () =>
    import("./pages/SupportTicketNew"
    ),
);
const SupportTicketDetail = lazy(
  () =>
    import("./pages/SupportTicketDetail"
    ),
);
const MySupportTickets = lazy(
  () =>
    import("./pages/MySupportTickets"
    ),
);
const SupportManager = lazy(
  () =>
    import("./pages/SupportManager"
    ),
);
const ContentProtectionPage = lazy(
  () =>
    import("./pages/ContentProtectionPage"
    ),
);
const LoginPageDesigner = lazy(
  () =>
    import("./pages/LoginPageDesigner"
    ),
);

// Admin-only pages
const MealPlanner = lazy(
  () =>
    import("./pages/MealPlanner"),
);
const DiscontinuedProducts = lazy(
  () =>
    import("./pages/DiscontinuedProducts"
    ),
);
const ProductComparison = lazy(
  () =>
    import("./pages/ProductComparison"
    ),
);
const EmailTemplatePage = lazy(
  () =>
    import("./pages/EmailTemplatePage"
    ),
);
const EmailConfigPage = lazy(
  () =>
    import("./pages/EmailConfigPage"
    ),
);
const EmailCampaignsPage = lazy(
  () =>
    import("./pages/EmailCampaignsPage"
    ),
);
const EmailCampaignEditor = lazy(
  () =>
    import("./pages/EmailCampaignEditor"
    ),
);
const EmailListsPage = lazy(
  () =>
    import("./pages/EmailListsPage"),
);
const EmailListCreatePage = lazy(
  () =>
    import("./pages/EmailListCreatePage"
    ),
);
const EmailFlowsPage = lazy(
  () =>
    import("./pages/EmailFlowsPage"),
);
const EmailFlowEditor = lazy(
  () =>
    import("./pages/EmailFlowEditor"
    ),
);
const ColesScraperPage = lazy(
  () =>
    import("./pages/ColesScraperPage"
    ),
);
const ColesScraperProgress = lazy(
  () =>
    import("./pages/ColesScraperProgress"
    ),
);
const WooliesScraperPage = lazy(
  () =>
    import("./pages/WooliesScraperPage"
    ),
);
const AldiScraperPage = lazy(
  () =>
    import("./pages/AldiScraperPage"
    ),
);
const IgaScraperPage = lazy(
  () =>
    import("./pages/IgaScraperPage"
    ),
);
const AmazonScraperPage = lazy(
  () =>
    import("./pages/AmazonScraperPage"
    ),
);
const AmazonGtinReviewPage = lazy(
  () =>
    import("./pages/AmazonGtinReviewPage"
    ),
);
const CronAdminPage = lazy(
  () =>
    import("./pages/CronAdminPage"
    ),
);
const IOSBuilderPage = lazy(
  () =>
    import("./pages/IOSBuilderPage"
    ),
);
const MasterSitemapPage = lazy(
  () =>
    import("./pages/MasterSitemapPage"
    ),
);
const ProductDataAdmin = lazy(
  () =>
    import("./pages/ProductDataAdmin"
    ),
);
const GlobalIdentifiersAdmin = lazy(
  () =>
    import("./pages/GlobalIdentifiersAdmin"
    ),
);
const PriorityProducts = lazy(
  () =>
    import("./pages/PriorityProducts"
    ),
);
const PriorityProductCreate = lazy(
  () =>
    import("./pages/PriorityProductCreate"
    ),
);
const PriceChanges = lazy(
  () =>
    import("./pages/PriceChanges"),
);
const NewProducts = lazy(
  () =>
    import("./pages/NewProducts.jsx"
    ),
);
const CustomReports = lazy(
  () =>
    import("./pages/CustomReports"
    ),
);
const ExportLibrary = lazy(
  () =>
    import("./pages/ExportLibrary"
    ),
);
const ZunoPriceAnalysis = lazy(
  () =>
    import("./pages/ZunoPriceAnalysis"
    ),
);
const ZunoSavedAnalysis = lazy(
  () =>
    import("./pages/ZunoSavedAnalysis"
    ),
);
const ZunoNutritionAnalysis = lazy(
  () =>
    import("./pages/ZunoNutritionAnalysis"
    ),
);
const NewsArticleEditor = lazy(
  () =>
    import("./pages/NewsArticleEditor"
    ),
);
const NewsArticlesPage = lazy(
  () =>
    import("./pages/NewsArticlesPage"
    ),
);
const NewsArticleAnalyticsPage = lazy(
  () =>
    import("./pages/NewsArticleAnalyticsPage"
    ),
);
const NewsArticleAdmin = lazy(
  () =>
    import("./pages/NewsArticleAdmin"
    ),
);
const NewsArticleView = lazy(
  () =>
    import("./pages/NewsArticleView"
    ),
);
const ImageSelector = lazy(
  () =>
    import("./pages/ImageSelector"
    ),
);
const PlatformStatus = lazy(
  () =>
    import("./pages/PlatformStatus"
    ),
);
const FileCountPage = lazy(
  () =>
    import("./pages/FileCountPage"
    ),
);
const ScrapeDateAdminPage = lazy(
  () =>
    import("./pages/ScrapeDateAdminPage"
    ),
);
const CategoryPathAdminPage = lazy(
  () =>
    import("./pages/CategoryPathAdminPage"
    ),
);
const HotPricesImporterPage = lazy(
  () =>
    import("./pages/HotPricesImporterPage"
    ),
);
const DataHoldingsImporterPage = lazy(
  () =>
    import("./pages/DataHoldingsImporterPage"
    ),
);

const APP_NAME = "Zuno";

const PAGE_TITLES = {
  "/": "Dashboard",
  "/login": "Login",
  "/signup": "Create Account",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
  "/dashboard": "Dashboard",
  "/card-view": "Dashboard",
  "/barcode-lookup": "Barcode Lookup",
  "/shopping": "Shopping List",
  "/shopping-list": "Shopping List",
  "/recent-purchases": "Recent Purchases",
  "/watch-list": "Watch List",
  "/meals": "Created Meals",
  "/planner": "Weekly Meal Planner",
  "/meal/chat-meal-planner": "Chat Meal Planner Builder",
  "/meal/chat-meal-planner-builder": "Chat Meal Planner Builder",
  "/meal/chat-meal-planner-classic": "Chat Meal Planner Classic",
  "/meal/chat-meal-planner-advanced": "Chat Meal Planner Advanced",
  "/meal/chat-meal-planner-elevated": "Chat Meal Planner Elevated",
  "/manual-meal-prep/new": "Create Meal",
  "/meal/manual-meal-prep/new": "Create Meal",
  "/profile": "Profile",
  "/subscribe": "Subscribe",
  "/subscription": "Subscription Settings",
  "/premium": "Premium",
  "/contact": "Contact",
  "/support-tickets": "My Support Tickets",
  "/meal-planner": "Meal Planner",
  "/discontinued-products": "Discontinued Products",
  "/product-comparison": "Product Comparison",
  "/admin/coles-scraper": "Coles Scraper",
  "/admin/coles-scraper-progress": "Coles Scraper Progress",
  "/admin/woolies-scraper": "Woolies Scraper",
  "/admin/aldi-scraper": "Aldi Scraper",
  "/admin/iga-scraper": "IGA Scraper",
  "/admin/amazon-scraper": "Amazon Scraper",
  "/admin/amazon-gtin-review": "Amazon GTIN Review",
  "/admin/cron-admin": "Cron Admin",
  "/admin/ios-builder": "iOS Builder",
  "/admin/master-sitemap": "Master Sitemap",
  "/admin/coles-latest": "Product Data Sync",
  "/admin/latest-extract": "Product Data Sync",
  "/admin/image-selector": "Image Selector",
  "/admin/barcode-scanner": "Barcode Scanner",
  "/admin/global-identifier-key": "Global Identifier Key",
  "/admin/priority-products": "Priority Products",
  "/admin/priority-products/new": "New Priority Product",
  "/admin/platform-status": "Platform Status",
  "/admin/file-count": "File Count",
  "/admin/scrape-date": "Scrape Date Admin",
  "/admin/category-paths": "Category Path Review",
  "/admin/cardview-weekly": "Product Data Sync",
  "/admin/hotprices-importer": "HotPrices Importer",
  "/admin/data-holdings-importer": "Data Holdings Importer",
  "/admin/content-protection": "Content Protection",
  "/admin/login-page-designer": "Login Page Designer",
  "/admin/email-template": "Email Template",
  "/admin/email-config": "Email Config",
  "/admin/email-lists": "Email Lists",
  "/admin/email-lists/create": "Create Email List",
  "/admin/email-flows": "Email Flows",
  "/admin/email-campaigns": "Email Campaigns",
  "/admin/email-campaigns/new": "New Email Campaign",
  "/admin/stripe-config": "Stripe Config",
  "/admin/subscriptions": "Subscriptions",
  "/admin/package-permissions": "Package Permissions",
  "/admin/stripe-events": "Stripe Events",
  "/admin/stripe-analytics": "Stripe Analytics",
  "/admin/users": "User Management",
  "/admin/user-audit-log": "User Audit Log",
  "/admin/product-analytics": "Product Analytics",
  "/admin/support-tickets": "Support Tickets",
  "/admin/support-tickets/new": "New Support Ticket",
  "/admin/support-manager": "Support Manager",
  "/admin/new-products": "New Products",
  "/admin/discontinued-products": "Discontinued Products",
  "/admin/price-changes": "Price Changes",
  "/admin/custom-reports": "Custom Reports",
  "/admin/export-library": "Export Library",
  "/admin/zuno": "Zuno Analytics",
  "/admin/zuno-saved": "Zuno - Saved Analysis",
  "/admin/zuno-nutrition": "Zuno - Nutrition",
  "/admin/news-articles": "News Articles",
  "/admin/enterprise-news-articles": "Enterprise News Articles",
  "/admin/add-news-article": "Add News Article",
  "/admin/news-article-admin": "News Article Admin",
  "/admin/news-article-analytics": "News Analytics",
};

const DYNAMIC_PAGE_TITLES = [
  { pattern: /^\/product\/[^/]+$/, title: "Product Details" },
  { pattern: /^\/meal\/manual-meal-prep\/[^/]+$/, title: "Meal Prep" },
  { pattern: /^\/manual-meal-prep\/[^/]+$/, title: "Meal Prep" },
  { pattern: /^\/meal\/[^/]+$/, title: "Meal Session" },
  { pattern: /^\/support-tickets\/[^/]+$/, title: "Support Ticket Details" },
  { pattern: /^\/admin\/email-flows\/[^/]+$/, title: "Email Flow Editor" },
  {
    pattern: /^\/admin\/email-campaigns\/[^/]+\/edit$/,
    title: "Edit Email Campaign",
  },
  {
    pattern: /^\/admin\/email-campaigns\/[^/]+\/view$/,
    title: "Email Campaign",
  },
  { pattern: /^\/admin\/stripe-events\/[^/]+$/, title: "Stripe Event Details" },
  { pattern: /^\/admin\/users\/[^/]+$/, title: "Edit User" },
  {
    pattern: /^\/admin\/support-tickets\/[^/]+$/,
    title: "Support Ticket Details",
  },
  {
    pattern: /^\/admin\/news-articles\/[^/]+\/edit$/,
    title: "Edit News Article",
  },
  { pattern: /^\/admin\/news-articles\/[^/]+$/, title: "News Article" },
];

function getPageTitle(pathname) {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const staticTitle = PAGE_TITLES[normalizedPath];
  const dynamicTitle = DYNAMIC_PAGE_TITLES.find(({ pattern }) =>
    pattern.test(normalizedPath),
  )?.title;

  return `${staticTitle || dynamicTitle || "Dashboard"} | ${APP_NAME}`;
}

function App() {
  const location = useLocation();
  const user = getCurrentUser();
  const [contentProtected, setContentProtected] = useState(false);

  useEffect(() => {
    document.title = getPageTitle(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    fetchContentProtection()
      .then((data) => {
        if (
          data.enabled &&
          (!user || !(data.whitelist || []).includes(user.user_group))
        ) {
          setContentProtected(true);
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!contentProtected) return;
    const msg = (e) => {
      e.preventDefault();
      alert("You need a subscription to copy content");
      if (e.clipboardData) {
        e.clipboardData.setData(
          "text/plain",
          "You need a subscription to copy content",
        );
      }
    };
    const blockPrint = (e) => {
      e.preventDefault();
      alert("Printing disabled");
    };
    const keyHandler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (["c", "x", "v"].includes(k)) {
          e.preventDefault();
          alert("You need a subscription to copy content");
        }
        if (k === "p") {
          e.preventDefault();
          alert("Printing disabled");
        }
      }
    };
    document.addEventListener("copy", msg);
    document.addEventListener("cut", msg);
    document.addEventListener("paste", msg);
    document.addEventListener("contextmenu", msg);
    document.addEventListener("keydown", keyHandler);
    window.addEventListener("beforeprint", blockPrint);
    window.print = () => alert("Printing disabled");

    document.querySelectorAll("[data-obfuscate]").forEach((el) => {
      const text = el.textContent;
      el.innerHTML = "";
      text.split("").forEach((ch) => {
        const span = document.createElement("span");
        span.textContent = ch;
        el.appendChild(span);
      });
    });
    const desc = document.getElementById("home-desc");
    if (desc) {
      const text = desc.textContent;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.font = getComputedStyle(desc).font || "16px sans-serif";
      canvas.width = ctx.measureText(text).width + 20;
      canvas.height = parseInt(ctx.font, 10) + 20 || 40;
      ctx.font = getComputedStyle(desc).font || "16px sans-serif";
      ctx.fillText(text, 10, canvas.height / 2);
      desc.textContent = "";
      desc.appendChild(canvas);
    }

    return () => {
      document.removeEventListener("copy", msg);
      document.removeEventListener("cut", msg);
      document.removeEventListener("paste", msg);
      document.removeEventListener("contextmenu", msg);
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("beforeprint", blockPrint);
    };
  }, [contentProtected]);

  return (
    <>
      <GlobalLoadingOverlay />
      {contentProtected && (
        <div id="scrape-trap" style={{ display: "none" }}>
          trap
        </div>
      )}
      <Suspense
        fallback={
          <div className="page-loading" role="status" aria-live="polite">
            <LoadingSpinner size="lg" label="Loading…" />
          </div>
        }
      >
        <Routes>
          {/* ---------- Public (unauthenticated) ---------- */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* ---------- Authenticated (non-admin + admin) ---------- */}
          <Route
            element={
              <PrivateRoute>
                <AccessProvider>
                  <UserAuditTracker />
                  <PermissionRoute>
                    <Layout />
                  </PermissionRoute>
                </AccessProvider>
              </PrivateRoute>
            }
          >
            {/* Default “dashboard” (CardView) */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<CardView />} />

            <Route path="/card-view" element={<Navigate to="/dashboard" replace />} />

            {/* Core browsing */}
            <Route path="/barcode-lookup" element={<BarcodeLookup />} />
            <Route path="/product/:id" element={<ProductDetails />} />

            {/* -------- Shopping section -------- */}
            <Route
              path="/shopping"
              element={<Navigate to="/shopping-list" replace />}
            />
            <Route path="/shopping-list" element={<ShoppingList />} />
            <Route path="/recent-purchases" element={<RecentPurchases />} />
            <Route path="/watch-list" element={<WatchList />} />

            {/* -------- Meals section -------- */}
            <Route path="/meals" element={<Meals />} />
            <Route path="/planner" element={<Planner />} />
            <Route
              path="/meal/chat-meal-planner"
              element={<ChatMealPlannerBuilder />}
            />
            <Route
              path="/meal/chat-meal-planner-builder"
              element={<ChatMealPlannerBuilder />}
            />
            <Route
              path="/meal/chat-meal-planner-classic"
              element={<ChatMealPlanner />}
            />
            <Route
              path="/meal/chat-meal-planner-advanced"
              element={<ChatMealPlannerAdvanced />}
            />
            <Route
              path="/meal/chat-meal-planner-elevated"
              element={<ChatMealPlannerBuilder />}
            />
            <Route path="/meal/:sessionId" element={<MealSessionPage />} />
            <Route
              path="/meal/manual-meal-prep/:sessionId"
              element={<MealPrepSessionPage />}
            />
            <Route
              path="/manual-meal-prep/:sessionId"
              element={<MealPrepSessionPage />}
            />

            {/* Profile and subscription */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/subscription" element={<SubscriptionSettings />} />
            <Route
              path="/premium"
              element={
                <PremiumRoute>
                  <PremiumContent />
                </PremiumRoute>
              }
            />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/support-tickets" element={<MySupportTickets />} />
            <Route
              path="/support-tickets/:id"
              element={<SupportTicketDetail />}
            />

            {/* ---------- Admin-only additions ---------- */}
            <Route
              path="/meal-planner"
              element={
                <AdminRoute>
                  <MealPlanner />
                </AdminRoute>
              }
            />
            <Route
              path="/discontinued-products"
              element={
                <AdminRoute>
                  <Navigate to="/admin/discontinued-products" replace />
                </AdminRoute>
              }
            />
            <Route
              path="/product-comparison"
              element={
                <AdminRoute>
                  <ProductComparison />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/coles-scraper"
              element={
                <AdminRoute>
                  <ColesScraperPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/coles-scraper-progress"
              element={
                <AdminRoute>
                  <ColesScraperProgress />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/woolies-scraper"
              element={
                <AdminRoute>
                  <WooliesScraperPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/aldi-scraper"
              element={
                <AdminRoute>
                  <AldiScraperPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/iga-scraper"
              element={
                <AdminRoute>
                  <IgaScraperPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/amazon-scraper"
              element={
                <AdminRoute>
                  <AmazonScraperPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/amazon-gtin-review"
              element={
                <AdminRoute>
                  <AmazonGtinReviewPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/cron-admin"
              element={
                <AdminRoute>
                  <CronAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ios-builder"
              element={
                <AdminRoute requireGroup>
                  <IOSBuilderPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/master-sitemap"
              element={
                <AdminRoute>
                  <MasterSitemapPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/coles-latest"
              element={
                <AdminRoute>
                  <ProductDataAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/latest-extract"
              element={
                <AdminRoute>
                  <ProductDataAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/image-selector"
              element={
                <AdminRoute>
                  <ImageSelector />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/barcode-scanner"
              element={
                <AdminRoute>
                  <AdminBarcodeScanner />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/global-identifier-key"
              element={
                <AdminRoute>
                  <GlobalIdentifiersAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/cardview-weekly"
              element={
                <AdminRoute>
                  <ProductDataAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/priority-products"
              element={
                <AdminRoute>
                  <PriorityProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/priority-products/new"
              element={
                <AdminRoute>
                  <PriorityProductCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/platform-status"
              element={
                <AdminRoute>
                  <PlatformStatus />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/file-count"
              element={
                <AdminRoute>
                  <FileCountPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/scrape-date"
              element={
                <AdminRoute>
                  <ScrapeDateAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/category-paths"
              element={
                <AdminRoute>
                  <CategoryPathAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/hotprices-importer"
              element={
                <AdminRoute>
                  <HotPricesImporterPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/data-holdings-importer"
              element={
                <AdminRoute>
                  <DataHoldingsImporterPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/content-protection"
              element={
                <AdminRoute>
                  <ContentProtectionPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/login-page-designer"
              element={
                <AdminRoute>
                  <LoginPageDesigner />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-template"
              element={
                <AdminRoute>
                  <EmailTemplatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-config"
              element={
                <AdminRoute>
                  <EmailConfigPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-lists"
              element={
                <AdminRoute>
                  <EmailListsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-lists/create"
              element={
                <AdminRoute>
                  <EmailListCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-flows"
              element={
                <AdminRoute>
                  <EmailFlowsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-flows/:id"
              element={
                <AdminRoute>
                  <EmailFlowEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-campaigns"
              element={
                <AdminRoute>
                  <EmailCampaignsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-campaigns/new"
              element={
                <AdminRoute>
                  <EmailCampaignEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-campaigns/:id/edit"
              element={
                <AdminRoute>
                  <EmailCampaignEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-campaigns/:id/view"
              element={
                <AdminRoute>
                  <EmailCampaignEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/stripe-config"
              element={
                <AdminRoute>
                  <StripeAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <AdminRoute>
                  <SubscriptionManager />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/package-permissions"
              element={
                <AdminRoute>
                  <PackagePermissionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/stripe-events"
              element={
                <AdminRoute>
                  <StripeEventsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/stripe-analytics"
              element={
                <AdminRoute>
                  <StripeAnalyticsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/stripe-events/:id"
              element={
                <AdminRoute>
                  <StripeEventDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <UsersAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/user-audit-log"
              element={
                <AdminRoute>
                  <UserAuditLogPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/product-analytics"
              element={
                <AdminRoute>
                  <ProductAnalyticsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <AdminRoute>
                  <UserEditPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/support-tickets"
              element={
                <AdminRoute>
                  <SupportTicketsAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/support-tickets/new"
              element={
                <AdminRoute>
                  <SupportTicketNew />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/support-manager"
              element={
                <AdminRoute>
                  <SupportManager />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/support-tickets/:id"
              element={
                <AdminRoute>
                  <SupportTicketDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/new-products"
              element={
                <AdminRoute>
                  <NewProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/discontinued-products"
              element={
                <AdminRoute>
                  <DiscontinuedProducts />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/price-changes"
              element={
                <AdminRoute>
                  <PriceChanges />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/custom-reports"
              element={
                <AdminRoute>
                  <CustomReports />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/export-library"
              element={
                <AdminRoute>
                  <ExportLibrary />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/zuno"
              element={
                <AdminRoute>
                  <ZunoPriceAnalysis />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/zuno-saved"
              element={
                <AdminRoute>
                  <ZunoSavedAnalysis />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/zuno-nutrition"
              element={
                <AdminRoute>
                  <ZunoNutritionAnalysis />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/news-articles"
              element={
                <AdminRoute>
                  <NewsArticlesPage category="news" />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/enterprise-news-articles"
              element={
                <AdminRoute>
                  <NewsArticlesPage category="enterprise" />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/add-news-article"
              element={
                <AdminRoute>
                  <NewsArticleEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/news-articles/:id/edit"
              element={
                <AdminRoute>
                  <NewsArticleEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/news-article-admin"
              element={
                <AdminRoute>
                  <NewsArticleAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/news-articles/:id"
              element={
                <AdminRoute>
                  <NewsArticleView />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/news-article-analytics"
              element={
                <AdminRoute>
                  <NewsArticleAnalyticsPage />
                </AdminRoute>
              }
            />
          </Route>

          {/* ---------- Catch-all ---------- */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
